import { useEffect, useState } from "react";
import { Crown, Trash2, Check, RefreshCw, Inbox, Sparkles } from "lucide-react";
import api, { formatErr } from "@/lib/api";

export default function KingRequestInbox() {
  const [reqs, setReqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchReqs = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/king-requests");
      setReqs(data);
      setError("");
    } catch (e) {
      setError(formatErr(e.response?.data?.detail) || "Couldn't load requests");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchReqs(); }, []);

  const handleToggleStatus = async (r) => {
    const next = r.status === "new" ? "reviewed" : r.status === "reviewed" ? "played" : "new";
    try {
      await api.patch(`/king-requests/${r.id}`, { status: next });
      fetchReqs();
    } catch (e) {
      setError(formatErr(e.response?.data?.detail));
    }
  };

  const handleDelete = async (r) => {
    if (!window.confirm("Delete this royal request?")) return;
    try {
      await api.delete(`/king-requests/${r.id}`);
      fetchReqs();
    } catch (e) {
      setError(formatErr(e.response?.data?.detail));
    }
  };

  const unread = reqs.filter((r) => r.status === "new").length;

  return (
    <section
      className="mt-8 p-6 rounded-xl relative overflow-hidden"
      data-testid="king-request-inbox"
      style={{
        background: "linear-gradient(180deg, #1a1108 0%, #0a0606 100%)",
        border: "1px solid rgba(212, 175, 55, 0.3)",
        boxShadow: "0 0 40px rgba(212, 175, 55, 0.06), inset 0 1px 0 rgba(255, 215, 0, 0.08)",
      }}
    >
      <div
        className="absolute top-0 left-0 right-0 h-[2px]"
        style={{ background: "linear-gradient(90deg, transparent, #d4af37 30%, #ffd700 50%, #d4af37 70%, transparent)" }}
      />
      <Crown size={140} className="absolute -top-6 -right-6 opacity-5 pointer-events-none text-[#ffd700]" strokeWidth={1} />

      <div className="flex items-center justify-between mb-5 flex-wrap gap-3 relative">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Crown size={16} className="text-[#ffd700]" />
            <span className="text-[10px] font-mono uppercase tracking-[0.25em]" style={{ color: "#d4af37" }}>
              Hidden Chamber
            </span>
            <Sparkles size={11} className="text-[#ffd700] animate-pulse" />
            {unread > 0 && (
              <span
                className="text-[10px] font-mono px-2 py-0.5 rounded-full font-bold"
                style={{ background: "linear-gradient(135deg, #ffd700, #b8860b)", color: "#1a1108" }}
                data-testid="king-unread-badge"
              >
                {unread} NEW
              </span>
            )}
          </div>
          <h2
            className="font-display text-xl font-black"
            style={{ background: "linear-gradient(180deg, #ffd700 0%, #d4af37 50%, #b8860b 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}
          >
            Royal Requests
          </h2>
        </div>
        <button
          onClick={fetchReqs}
          className="btn-ghost flex items-center gap-1.5"
          style={{ color: "#d4af37", borderColor: "rgba(212, 175, 55, 0.3)" }}
          data-testid="king-refresh-button"
        >
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      {error && (
        <p className="text-[#ff5566] text-sm font-mono mb-3" data-testid="king-error-row">{error}</p>
      )}

      {loading && reqs.length === 0 ? (
        <p className="text-[#d4af37]/70 text-sm font-mono py-6 text-center">Loading royal stack...</p>
      ) : reqs.length === 0 ? (
        <div className="text-center py-10 border border-dashed rounded-lg" style={{ borderColor: "rgba(212, 175, 55, 0.2)" }} data-testid="king-empty">
          <Inbox size={28} className="mx-auto mb-3 opacity-40" style={{ color: "#d4af37" }} />
          <p className="text-[#d4af37]/80 text-sm">No royal requests yet.</p>
          <p className="text-[#6b5a2c] text-xs font-mono mt-1">
            The chamber is hidden — only true fans can find it.
          </p>
        </div>
      ) : (
        <ul className="space-y-2 relative" data-testid="king-list">
          {reqs.map((r) => {
            const isNew = r.status === "new";
            return (
              <li
                key={r.id}
                data-testid={`king-row-${r.id}`}
                className="flex items-start gap-3 p-3 rounded-lg border transition-colors"
                style={{
                  background: isNew ? "rgba(212, 175, 55, 0.06)" : "#0d0805",
                  borderColor: isNew ? "rgba(212, 175, 55, 0.4)" : "rgba(212, 175, 55, 0.15)",
                }}
              >
                <Crown size={18} className="flex-shrink-0 mt-0.5" style={{ color: isNew ? "#ffd700" : "#6b5a2c" }} />

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-0.5">
                    <p className="text-sm font-semibold text-white truncate">{r.name || "Anonymous King"}</p>
                    {isNew && <span className="text-[9px] font-mono text-[#ffd700]">● NEW</span>}
                    {r.status === "reviewed" && <span className="text-[9px] font-mono text-[#d4af37]/60">REVIEWED</span>}
                    {r.status === "played" && <span className="text-[9px] font-mono text-[var(--neon-cyan)]">PLAYED ON AIR</span>}
                  </div>
                  <p className="text-sm font-medium" style={{ color: "#ffd700" }}>♪ {r.song}</p>
                  {r.message && (
                    <p className="text-xs text-[#d4af37]/80 mt-1 italic">"{r.message}"</p>
                  )}
                  <p className="text-[10px] text-[#6b5a2c] font-mono mt-1">
                    {new Date(r.created_at).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" })}
                  </p>
                </div>

                <div className="flex flex-col gap-1.5 flex-shrink-0">
                  <button
                    type="button"
                    onClick={() => handleToggleStatus(r)}
                    className="btn-ghost !py-1 !px-2 flex items-center gap-1"
                    style={{ color: "#d4af37", borderColor: "rgba(212, 175, 55, 0.3)" }}
                    data-testid={`king-toggle-${r.id}`}
                    title="Cycle status (new → reviewed → played)"
                  >
                    <Check size={12} />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(r)}
                    className="btn-danger flex items-center gap-1"
                    data-testid={`king-delete-${r.id}`}
                    aria-label="Delete"
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
